import { Modal, Select } from 'antd'
import { parse } from 'papaparse'
import { useCallback, useEffect, useMemo, useState, VFC } from 'react'
import { useTranslation } from 'react-i18next'

import { ImportAnnouncementsPageHeader, ImportAnnouncementsUploadCvs } from '@/components/pages/Announcements/Import'
import { useImportAnnouncement } from '@/pages/Announcements/Import/Import.hooks'
import { useLanguages } from '@/pages/Languages/Languages.hooks'

const ImportAnnoucement: VFC = () => {
  const { loading, importAnnouncement } = useImportAnnouncement()
  const [showsModal, setShowsModal] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [encoding, setEncoding] = useState('shift-jis')
  const { getLanguages } = useLanguages()
  const [t] = useTranslation()

  const handleFileSelected = (data: FormData) => {
    const file = data.get('file') as File
    setFile(file)
  }

  const headers = useMemo(() => {
    const result = new Set<string>()
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => result.add(key))
    })
    return Array.from(result)
  }, [rows])

  const handleChange = (header: string) => (value: string) => {
    // remove old header
    setMapping({
      ...mapping,
      [value]: header,
    })
  }

  const handleConfirm = useCallback(() => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mapping', JSON.stringify(mapping))
    formData.append('encoding', encoding)

    importAnnouncement(formData)
  }, [encoding, file, importAnnouncement, mapping])

  const handleChangeEncoding = useCallback((value: string) => {
    setEncoding(value)
  }, [])

  useEffect(() => {
    if (!file) return
    const fileReader = new FileReader()
    fileReader.addEventListener('load', () => {
      const result = parse<Record<string, string>>(fileReader.result as string, { header: true })
      setRows(result.data)
      setShowsModal(true)
      setMapping({})
    })
    fileReader.readAsText(file, encoding)
  }, [encoding, file])

  useEffect(() => {
    getLanguages().then()
  }, [getLanguages])

  return (
    <>
      <ImportAnnouncementsPageHeader />
      <ImportAnnouncementsUploadCvs loading={loading} onUpload={handleFileSelected} />
      {file && (
        <Modal
          title={t('pages.announcements.importFromCsv.importModalTitle')}
          visible={showsModal}
          width={1000}
          onCancel={() => {
            setShowsModal(false)
            setFile(null)
          }}
          onOk={handleConfirm}
        >
          <Select defaultValue="" onChange={handleChangeEncoding} value={encoding}>
            <Select.Option value="utf-8">UTF-8</Select.Option>
            <Select.Option value="shift-jis">Shift-JIS</Select.Option>
          </Select>
          <table>
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header}>
                    {header}
                    <br />
                    <Select defaultValue="" onChange={handleChange(header)}>
                      <Select.Option value="">{t('pages.announcements.importFromCsv.select')}</Select.Option>
                      <Select.Option value="ja">{t('pages.announcements.importFromCsv.ja')}</Select.Option>
                      <Select.Option value="en">{t('pages.announcements.importFromCsv.en')}</Select.Option>
                      <Select.Option value="cn">{t('pages.announcements.importFromCsv.cn')}</Select.Option>
                      <Select.Option value="th">{t('pages.announcements.importFromCsv.th')}</Select.Option>
                      <Select.Option value="vi">{t('pages.announcements.importFromCsv.vi')}</Select.Option>
                    </Select>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row, index) => (
                <tr key={index}>
                  {headers.map((header, index) => (
                    <td key={index}>{row[header]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </>
  )
}

ImportAnnoucement.displayName = 'ImportAnnoucement'
export default ImportAnnoucement
